/**
 * @fileoverview Company Projects Context
 * 
 * @description
 * Provides information about the company's projects availability.
 * This context tracks whether the company has any projects,
 * enabling proper visibility of menus that require project existence.
 * 
 * @version 1.0.0
 * @date 2026-01-28
 */

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { bootstrapGet } from '../utils/bootstrapApi';
import { useAuth } from './AuthContext';
import { useAccessManifest } from './AccessManifestContext';

const CompanyProjectsContext = createContext(null);

export const CompanyProjectsProvider = ({ children }) => {
  const { user } = useAuth();
  const { loaded: manifestLoaded, hasCapability } = useAccessManifest();
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchProjects = useCallback(async () => {
    if (!user) {
      setProjects([]);
      setProjectsLoaded(true);
      return;
    }

    // Wait for manifest before hitting tenant DB — avoids racing manifest on cold start (R23).
    if (!manifestLoaded) {
      return;
    }

    if (!hasCapability('projects.read')) {
      setProjects([]);
      setProjectsLoaded(true);
      return;
    }

    setLoading(true);
    try {
      const assignedOnly = user?.role && !['admin', 'superadmin'].includes(user.role);
      const endpoint = `/projects?view=selector${assignedOnly ? '&assigned_only=true' : ''}`;
      const response = await bootstrapGet(endpoint);
      setProjects(response?.data || []);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail ?? err?.response?.data;
      console.error(
        'Error fetching projects:',
        status != null ? `HTTP ${status}` : err?.message || err?.code || 'network error',
        detail || err?.response?.data || ''
      );
      setProjects([]);
    } finally {
      setLoading(false);
      setProjectsLoaded(true);
    }
  }, [user?.id, user?.username, user?.role, manifestLoaded, hasCapability]);

  useEffect(() => {
    setProjectsLoaded(false);
    fetchProjects();
  }, [fetchProjects]);

  const projectCount = projects.length;
  const hasProjects = projectCount > 0;
  const configuredProjects = projects.filter(p => p.is_configured);

  const refreshProjectCount = useCallback(() => {
    return fetchProjects();
  }, [fetchProjects]);

  const value = {
    projects,
    configuredProjects,
    projectCount,
    hasProjects,
    projectsLoaded,
    loading,
    refreshProjectCount,
  };

  return (
    <CompanyProjectsContext.Provider value={value}>
      {children}
    </CompanyProjectsContext.Provider>
  );
};

/**
 * Hook to access company projects information
 * @returns {Object} Company projects context value
 */
export const useCompanyProjects = () => {
  const context = useContext(CompanyProjectsContext);
  if (!context) {
    throw new Error('useCompanyProjects must be used within a CompanyProjectsProvider');
  }
  return context;
};

export default CompanyProjectsContext;
